/**
 * The one HTTP client (ARCHITECTURE.md §6, wp/shell §4.12). Every module calls the API through `http`:
 *
 * - base URL and `Api-Key` from the validated config; `Auth-Key` from the auth store;
 * - with a session, `company_id` / `software_id` are added **only when the caller did not set them** (a per-call
 *   `software_id` wins — employees OPEN-1), `company_id` is omitted without a workspace (OPEN-13), and a POST without
 *   a body gets them too (P4);
 * - the `{status, code, data, message}` envelope becomes `ApiResponse<T>`; any error — HTTP or an envelope with
 *   `status: "error"` — rejects with an `ApiError` carrying the server message;
 * - a 401 calls the handler the shell registers with `onUnauthorized()` and **still rejects** (P1); nothing here
 *   touches `window.location`;
 * - no global spinner (per-query loading), and no rewriting of `undefined` / `null` into `""` (P3): build bodies
 *   with the values you mean to send;
 * - `lang` is added to every request body the caller sends (legacy did it for every body, reading it from the page
 *   URL — NEW-5); the value is the active UI locale the shell registers with `setRequestLocale()`.
 */
import axios, {
  AxiosHeaders,
  type AxiosInstance,
  type AxiosProgressEvent,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'

import { getConfig } from '@/app/config'
import { useAuthStore } from '@/app/stores/auth'

declare module 'axios' {
  interface AxiosRequestConfig {
    /** No `Auth-Key` and no session defaults: login, activation, registration (wp/shell §4.12). */
    anonymous?: boolean
    /** No `company_id` / `software_id` defaults. */
    skipSessionDefaults?: boolean
    /** Do not call the registered 401 handler (the caller handles the refusal itself). */
    skipUnauthorizedHandler?: boolean
  }
}

/** The body of every JSON answer (OpenAPI `Envelope`). */
export interface ApiEnvelope<T = unknown> {
  status: 'success' | 'error'
  /** Same as the HTTP status for 200/400/401/403/404/503; any other code arrives with HTTP 200. */
  code: number
  data: T
  /** Translated message: always with 200 and 400; with 401/403/404 only when the action gives a reason. */
  message?: string
  /** Untranslated message, when the action gives one. */
  debug_message?: string
}

export interface ApiResponse<T> {
  data: T
  /** Envelope `code`. */
  code: number
  /** Translated server message (`successMessage()` shows it), `null` when none was sent. */
  message: string | null
  /** HTTP status. */
  status: number
}

export interface ApiErrorInit {
  status: number
  code?: number | null
  serverMessage?: string | null
  debugMessage?: string | null
  data?: unknown
  method?: string
  url?: string
  anonymous?: boolean
  cause?: unknown
}

/** A refused or failed call. `serverMessage` is what legacy `errorMessage()` showed (`data.message`). */
export class ApiError extends Error {
  /** HTTP status; `0` when no answer arrived (network, CORS, timeout). */
  readonly status: number
  /** Envelope `code`, when the answer had an envelope. */
  readonly code: number | null
  readonly serverMessage: string | null
  readonly debugMessage: string | null
  /** Envelope `data` (some 400 answers carry details: rejected import lines, the refused record). */
  readonly data: unknown
  readonly method: string | undefined
  readonly url: string | undefined
  /** The request was sent without a session (login-type calls). */
  readonly anonymous: boolean

  constructor(message: string, init: ApiErrorInit) {
    super(message, init.cause === undefined ? undefined : { cause: init.cause })
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code ?? null
    this.serverMessage = init.serverMessage ?? null
    this.debugMessage = init.debugMessage ?? null
    this.data = init.data
    this.method = init.method
    this.url = init.url
    this.anonymous = init.anonymous ?? false
  }

  get isUnauthorized(): boolean {
    return this.status === 401 || this.code === 401
  }

  get isNetworkError(): boolean {
    return this.status === 0
  }
}

export function isEnvelope(body: unknown): body is ApiEnvelope {
  if (typeof body !== 'object' || body === null) return false
  const record = body as Record<string, unknown>
  return (
    (record.status === 'success' || record.status === 'error') && typeof record.code === 'number' && 'data' in record
  )
}

let requestLocale: (() => string | null) | null = null

/** The shell registers where `lang` comes from (the active UI locale). `null` stops adding it. */
export function setRequestLocale(provider: (() => string | null) | null): void {
  requestLocale = provider
}

/** Adds `lang` to a JSON / form body that does not carry it. */
export function applyRequestLocale(config: InternalAxiosRequestConfig, lang: string): void {
  const data: unknown = config.data
  if (data instanceof FormData || data instanceof URLSearchParams) {
    if (!data.has('lang')) data.append('lang', lang)
  } else if (isPlainObject(data) && data.lang === undefined) {
    config.data = { ...data, lang }
  }
}

export type UnauthorizedHandler = (error: ApiError) => void

let unauthorizedHandler: UnauthorizedHandler | null = null

/** The shell registers what a 401 does (clear the session, go to login — or not, on a public page). */
export function onUnauthorized(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && Object.getPrototypeOf(value) === Object.prototype
}

function queryKeysOf(config: InternalAxiosRequestConfig): Set<string> {
  const keys = new Set<string>()
  const url = config.url ?? ''
  const query = url.includes('?') ? url.slice(url.indexOf('?') + 1) : ''
  new URLSearchParams(query).forEach((_, key) => keys.add(key))
  const params: unknown = config.params
  if (params instanceof URLSearchParams) params.forEach((_, key) => keys.add(key))
  else if (isPlainObject(params)) Object.entries(params).forEach(([key, value]) => value !== undefined && keys.add(key))
  return keys
}

/** `company_id` / `software_id` where the caller did not set them (wp/shell §4.12, intended behaviour). */
export function applySessionDefaults(config: InternalAxiosRequestConfig, defaults: Record<string, number>): void {
  const entries = Object.entries(defaults)
  if (!entries.length) return
  const method = (config.method ?? 'get').toLowerCase()
  if (method === 'get' || method === 'delete' || method === 'head') {
    const present = queryKeysOf(config)
    const params: unknown = config.params
    if (params instanceof URLSearchParams) {
      for (const [key, value] of entries) if (!present.has(key)) params.append(key, String(value))
    } else {
      const merged: Record<string, unknown> = isPlainObject(params) ? { ...params } : {}
      for (const [key, value] of entries) if (!present.has(key)) merged[key] = value
      config.params = merged
    }
    return
  }
  const data: unknown = config.data
  if (data === undefined || data === null) {
    config.data = Object.fromEntries(entries)
  } else if (data instanceof FormData || data instanceof URLSearchParams) {
    for (const [key, value] of entries) if (!data.has(key)) data.append(key, String(value))
  } else if (isPlainObject(data)) {
    const merged: Record<string, unknown> = { ...data }
    for (const [key, value] of entries) if (merged[key] === undefined) merged[key] = value
    config.data = merged
  }
}

function prepareRequest(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const { apiUrl, apiKey } = getConfig()
  config.baseURL ??= apiUrl
  const headers = AxiosHeaders.from(config.headers)
  headers.set('Api-Key', apiKey)
  const method = (config.method ?? 'get').toLowerCase()
  const callerBody = method !== 'get' && method !== 'delete' && method !== 'head' && config.data != null
  if (!config.anonymous) {
    const auth = useAuthStore()
    if (auth.token) {
      headers.set('Auth-Key', auth.token)
      if (!config.skipSessionDefaults) {
        const defaults: Record<string, number> = {}
        if (auth.companyId) defaults.company_id = auth.companyId
        if (auth.softwareId) defaults.software_id = auth.softwareId
        applySessionDefaults(config, defaults)
      }
    }
  }
  const lang = callerBody ? requestLocale?.() : null
  if (lang) applyRequestLocale(config, lang)
  config.headers = headers
  return config
}

function errorFrom(
  response: AxiosResponse | undefined,
  config: AxiosRequestConfig | undefined,
  cause: unknown,
): ApiError {
  const body: unknown = response?.data
  const envelope = isEnvelope(body) ? body : null
  const status = response?.status ?? 0
  const serverMessage = envelope?.message ?? null
  const message =
    serverMessage ??
    envelope?.debug_message ??
    (status ? `Request failed with status ${status}` : 'The API could not be reached')
  return new ApiError(message, {
    status,
    code: envelope?.code ?? null,
    serverMessage,
    debugMessage: envelope?.debug_message ?? null,
    data: envelope ? envelope.data : body,
    method: config?.method?.toUpperCase(),
    url: config?.url,
    anonymous: Boolean(config?.anonymous),
    cause,
  })
}

function reject(error: ApiError, config: AxiosRequestConfig | undefined): Promise<never> {
  if (error.isUnauthorized && !config?.skipUnauthorizedHandler) unauthorizedHandler?.(error)
  return Promise.reject(error)
}

/** Shared by `http.*` and `http.raw`; also usable on its own (tests). */
export function createHttpInstance(): AxiosInstance {
  const instance = axios.create()
  instance.interceptors.request.use(prepareRequest)
  instance.interceptors.response.use(
    (response) => {
      const body: unknown = response.data
      if (isEnvelope(body) && body.status === 'error')
        return reject(errorFrom(response, response.config, null), response.config)
      return response
    },
    (error: unknown) => {
      if (axios.isCancel(error)) return Promise.reject(error)
      if (error instanceof ApiError) return Promise.reject(error)
      if (axios.isAxiosError(error)) return reject(errorFrom(error.response, error.config, error), error.config)
      return Promise.reject(error)
    },
  )
  return instance
}

const instance = createHttpInstance()

function toApiResponse<T>(response: AxiosResponse): ApiResponse<T> {
  const body: unknown = response.data
  if (!isEnvelope(body)) {
    throw new ApiError('The API answered outside the envelope; use http.raw for this endpoint', {
      status: response.status,
      data: body,
      method: response.config.method?.toUpperCase(),
      url: response.config.url,
    })
  }
  return { data: body.data as T, code: body.code, message: body.message ?? null, status: response.status }
}

export interface UploadOptions extends AxiosRequestConfig {
  /** Called with a fraction between 0 and 1. */
  onProgress?: (fraction: number, event: AxiosProgressEvent) => void
}

export const http = {
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return toApiResponse<T>(await instance.get(url, config))
  },
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return toApiResponse<T>(await instance.delete(url, config))
  },
  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return toApiResponse<T>(await instance.post(url, data, config))
  },
  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return toApiResponse<T>(await instance.put(url, data, config))
  },
  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return toApiResponse<T>(await instance.patch(url, data, config))
  },
  /** Multipart upload through the same client (headers, defaults, errors). */
  async upload<T>(url: string, form: FormData, options: UploadOptions = {}): Promise<ApiResponse<T>> {
    const { onProgress, ...config } = options
    return toApiResponse<T>(
      await instance.post(url, form, {
        ...config,
        onUploadProgress: onProgress
          ? (event) => onProgress(event.total ? event.loaded / event.total : 0, event)
          : config.onUploadProgress,
      }),
    )
  },
  /**
   * The underlying axios instance, for answers outside the envelope (`system/locales`, `?export=1` CSV, files).
   * Same headers, defaults and error handling; the caller reads `response.data` itself.
   */
  raw: instance,
}
